<template>
  <q-card class="full-height column bg-grey-1" style="overflow-y: auto">
    <q-card-section class="bg-primary text-white shrink shadow-2">
      <div class="text-subtitle1 text-weight-bold">Current PA Summary & Edits</div>
      <div class="text-caption">Adjust final outcomes, outs, and runs for this batter.</div>
    </q-card-section>

    <q-card-section class="col-grow q-gutter-y-md q-pa-sm">
      <!-- 1. FINAL PITCH -->
      <q-card flat bordered class="bg-white" v-if="lastPitch">
        <q-card-section class="bg-grey-2 border-bottom q-py-sm">
          <div class="text-subtitle2 text-weight-bold text-black">Final Pitch</div>
        </q-card-section>
        <q-card-section>
          <div class="row items-center q-col-gutter-md">
            <div class="col-12 relative-position">
              <q-select
                v-model="lastPitchOutcome"
                :options="pitchOptions"
                emit-value
                map-options
                label="Pitch Outcome"
                label-color="black"
                outlined
                dense
                @update:model-value="savePitchOutcome"
              />
              <HelpHint text="Changing to a Strikeout (K, -K) will automatically calculate an out." />
            </div>
          </div>
        </q-card-section>
      </q-card>

      <!-- 2. PLAY RESULT -->
      <q-card flat bordered class="bg-white" v-if="activePlay">
        <q-card-section class="bg-grey-2 border-bottom q-py-sm q-pa-sm">
          <div class="text-subtitle2 text-weight-bold text-black">Play Result</div>
        </q-card-section>
        <q-card-section>
          <div class="row items-center q-col-gutter-md">
            <div class="col-12 col-md-4">
              <q-select
                v-model="playOutcome"
                :options="playOptions"
                emit-value
                map-options
                label="Play Outcome"
                label-color="black"
                outlined
                dense
                @update:model-value="savePlayOutcome"
              />
            </div>
            <div class="col-6">
              <q-input v-model.number="playOuts" type="number" label="Outs" label-color="black" outlined dense min="0" max="3" @update:model-value="savePlayOuts" />
            </div>
            <div class="col-6">
              <q-input :model-value="playRuns" type="number" label="Runs" label-color="black" outlined dense min="0" @update:model-value="savePlayRuns" />
            </div>
          </div>
        </q-card-section>
      </q-card>

      <!-- 3. RUNNER ADVANCES -->
      <q-card flat bordered class="bg-white" v-if="runnerAdvances.length > 0">
        <q-card-section class="bg-grey-2 border-bottom q-py-sm">
          <div class="text-subtitle2 text-weight-bold text-black">Runner Advances</div>
        </q-card-section>
        <q-card-section class="q-pa-none">
          <q-list separator>
            <q-item v-for="adv in runnerAdvances" :key="adv.id" class="q-py-md">
              <q-item-section>
                <div class="text-weight-bold text-primary q-mb-sm">
                  Runner #{{ getRunnerUniform(adv.runnerId) }}
                </div>
                <div class="row items-center q-col-gutter-sm">
                  <div class="col-12 q-mb-sm">
                    <q-btn-toggle
                      :model-value="adv.isOut"
                      :options="[
      { label: 'Safe', value: false, color: 'positive' },
      { label: 'Out', value: true, color: 'negative' },
    ]"
                      spread
                      unelevated
                      @update:model-value="(val) => saveRunnerOut(adv.id, val)"
                    />
                  </div>
                  <div class="col-12 col-sm-6">
                    <q-select :model-value="adv.advanceReason" :options="runnerOptions" emit-value map-options label="Reason" label-color="black" outlined dense @update:model-value="(val) => saveRunnerReason(adv.id, val)" />
                  </div>
                  <div class="col-12 col-sm-6">
                    <q-input :model-value="adv.runsScored" type="number" label="Runs" label-color="black" outlined dense min="0" @update:model-value="saveRunnerRuns(adv.id, $event)" />
                  </div>
                </div>
              </q-item-section>
            </q-item>
          </q-list>
        </q-card-section>
      </q-card>

      <!-- EMPTY STATE -->
      <div
        v-if="!lastPitch && !activePlay && runnerAdvances.length === 0"
        class="text-center text-grey-6 q-pa-xl"
      >
        <q-icon name="info" size="xl" class="q-mb-sm opacity-50" />
        <div>No transactions recorded for this plate appearance yet.</div>
      </div>
    </q-card-section>
  </q-card>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import HelpHint from 'components/HelpHint.vue'
import { useGameStore } from 'stores/game-store'
import { useSettingsStore } from 'stores/settings-store'
import { db } from 'src/models/db'

const gameStore = useGameStore()
const settingsStore = useSettingsStore()

// --- SETTINGS STORE OPTIONS ---
const pitchOptions = computed(() => {
  // Combine primary categories (Ball, Strike, In Play) with the advanced options so they aren't locked out of basics
  const primary = settingsStore.primaryPitchCategories.map((o) => ({
    label: o.label,
    value: o.value,
  }))
  const advanced = settingsStore.pitchAdvancedOptions.map((o) => ({
    label: o.label,
    value: o.value,
  }))
  return [...primary, ...advanced]
})

const playOptions = computed(() => {
  // Combine primary and advanced play outcomes
  const primary = settingsStore.primaryPlayOutcomes.map((o) => ({ label: o.label, value: o.value }))
  const advanced = settingsStore.playAdvancedOutcomes.map((o) => ({
    label: o.label,
    value: o.value,
  }))
  return [...primary, ...advanced]
})

const runnerOptions = computed(() => {
  return settingsStore.runnerAdvanceOptions.map((o) => ({ label: o.label, value: o.value }))
})

// --- DATA BINDING ---
const currentTxns = computed(() => gameStore.currentGameTransactions)

const lastPitch = computed(() => {
  const pitches = currentTxns.value.filter((t) => t.eventType === 'PITCH')
  return pitches.sort((a, b) => a.sequence - b.sequence).pop()
})

const activePlay = computed(() => gameStore.activePlay)

const runnerAdvances = computed(() => {
  return currentTxns.value.filter((t) => t.eventType === 'RUNNER_ADVANCE')
})

// Local refs for v-model binding
const lastPitchOutcome = ref(lastPitch.value?.pitchOutcome || '')
const playOutcome = ref(activePlay.value?.outcome || '')
const playOuts = ref(activePlay.value?.outsRecorded || 0)
const playRuns = ref(activePlay.value?.runsScored || 0)

// Watchers to keep local refs in sync if store changes elsewhere
watch(
  lastPitch,
  (newVal) => {
    if (newVal) lastPitchOutcome.value = newVal.pitchOutcome
  },
  { deep: true },
)
watch(
  activePlay,
  (newVal) => {
    if (newVal) {
      playOutcome.value = newVal.outcome
      playOuts.value = newVal.outsRecorded || 0
      playRuns.value = newVal.runsScored || 0
    }
  },
  { deep: true },
)

// --- SAVE ACTIONS ---

const savePitchOutcome = async (val: string | null) => {
  if (!lastPitch.value || !val) return
  await gameStore.updatePitch(lastPitch.value.id, { pitchOutcome: val })
  // Note: Your `currentOuts` getter automatically picks up 'K' or '-K' from this transaction!
}

const savePlayOutcome = async (val: string | null) => {
  if (!activePlay.value || !val) return
  const play = activePlay.value
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  play.outcome = val as any
  await db.transactions.put(JSON.parse(JSON.stringify(play)))
}

const savePlayOuts = async (val: string | number | null) => {
  if (!activePlay.value) return
  const play = activePlay.value
  play.outsRecorded = Number(val) || 0
  await db.transactions.put(JSON.parse(JSON.stringify(play)))
}

const savePlayRuns = async (val: string | number | null) => {
  if (!activePlay.value) return
  const play = activePlay.value
  const newRuns = Number(val) || 0

  // Calculate the difference so we can safely adjust the global scoreboard
  const diff = newRuns - (play.runsScored || 0)

  play.runsScored = newRuns
  await db.transactions.put(JSON.parse(JSON.stringify(play)))

  if (diff !== 0 && gameStore.viewedPlateAppearance) {
    await gameStore.adjustScore(
      gameStore.viewedPlateAppearance.halfInning,
      gameStore.viewedPlateAppearance.inning - 1,
      diff,
      'Manual run adjustment on play',
    )
  }
}

const saveRunnerReason = async (id: string, val: string | null) => {
  if (!val) return
  const txn = runnerAdvances.value.find((t) => t.id === id)
  if (!txn) return
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  txn.advanceReason = val as any
  await db.transactions.put(JSON.parse(JSON.stringify(txn)))
}

const saveRunnerOut = async (id: string, val: boolean | null) => {
  const txn = runnerAdvances.value.find((t) => t.id === id)
  if (!txn) return
  txn.isOut = val ?? false
  await db.transactions.put(JSON.parse(JSON.stringify(txn)))
}

const saveRunnerRuns = async (id: string, val: string | number | null) => {
  const txn = runnerAdvances.value.find((t) => t.id === id)
  if (!txn) return

  const newRuns = Number(val) || 0
  // Calculate the difference so we can safely adjust the global scoreboard
  const diff = newRuns - (txn.runsScored || 0)

  txn.runsScored = newRuns
  await db.transactions.put(JSON.parse(JSON.stringify(txn)))

  if (diff !== 0 && gameStore.viewedPlateAppearance) {
    await gameStore.adjustScore(
      gameStore.viewedPlateAppearance.halfInning,
      gameStore.viewedPlateAppearance.inning - 1,
      diff,
      'Manual run adjustment on runner',
    )
  }
}

const getRunnerUniform = (id: number) => {
  const pa = gameStore.viewedPlateAppearance
  if (!pa) return '-'
  const lineup = pa.halfInning === 'top' ? gameStore.visitingLineup : gameStore.homeLineup

  // Need to map substitutions in case a pinch runner was inserted
  const allPlayers = lineup ? lineup.concat(lineup.flatMap((l) => l.substitutions || [])) : []
  const entry = allPlayers.find((e) => e.player.id === id)

  return entry?.player.uniformNumber || '-'
}
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid #e0e0e0;
}
</style>
